import React, { Dispatch, useContext, useMemo } from 'react';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import { DescriptorJblOptionText } from '@r6digital/rapidstor-types/types/Descriptor';

import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/free-regular-svg-icons';
import {
    faL, fas, faT, IconPrefix,
    IconName,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDealDisplay } from '@/journey-based-listing/hooks/useDealDisplay';
import Level1 from '@/assets/icon-floor-1.svg?react';
import Level0 from '@/assets/icon-floor-0.svg?react';

import { getEffectiveDealRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import DriveUp from '@/journey-based-listing/components/DriveUp';

import { getDealDefaultText } from '@/core/utils/dealDisplay';
import { formatDealCustomText } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { UpgradeDescriptor } from '@/features/rapidstor/UnitOptions';
import { resolveOptionCopy } from '@/journey-based-listing/utils/optionCopy';

import classes from '../Option.module.scss';
import unitListingClasses from '@/scss/screens/unit-listing.module.scss';

library.add(fas);
library.add(far);
library.add(faL);
library.add(faT);

interface Props extends React.HTMLAttributes<HTMLElement> {
    option: UnitType;
    dealObject?: Deal;
    initialRate?: number;
    desktopOption?: boolean;
    location: Location;
    selected: boolean;
    default?: boolean;
    showTotalPrice?: boolean;
    wizard?: boolean;
    optionText?: DescriptorJblOptionText;
    descriptor?: UpgradeDescriptor;

    setSelectedID: Dispatch<React.SetStateAction<string>>;
    onOptionSelect?: (option: UnitType, descriptor?: UpgradeDescriptor) => void;
}

export default function BaseOption({
    option,
    dealObject: dealObjectFromProps,
    initialRate,
    desktopOption,
    location,
    selected,
    default: defaultOption,
    showTotalPrice,
    wizard,
    optionText,
    descriptor,

    setSelectedID,
    onOptionSelect,
}: Props) {
    const { setSelectedDescriptor, setSelectedUnitType, reservationData } = useContext(JourneyContext);
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { getPriceDifferenceDisplay } = useDealDisplay();

    const formattedAccessLocation = useMemo(() => {
        switch (option.units[0]?.iEntryLoc) {
            case 1:
                return 'Drive Up Access';
            default:
                return 'Hallway Access';
        }
    }, [option.units]);

    // Shared with the customise-booking tracking events, so the copy they record is the
    // copy rendered here.
    const { heading: optionHeader, subheading: optionLabel } = useMemo(
        () => resolveOptionCopy({
            unitType: option,
            optionText,
            upgradeDescriptor: descriptor,
        }),
        [option, optionText, descriptor],
    );

    const makeFontAwesomeIcon = (prefix: string, iconName: string) => {
        if (!iconName) {
            return null;
        }

        const iconStyleMappings: Record<string, string> = {
            'fa-solid': 'fas',
            'fa-regular': 'faR',
            'fa-light': 'faL',
            'fa-thin': 'faT',
        };

        const iconParts = iconName.split('-');
        const formattedIconName = iconParts.slice(1).join('-') as IconName;
        const iconStyle = iconStyleMappings[prefix];
        return <FontAwesomeIcon icon={[iconStyle as IconPrefix, formattedIconName]} />;
    };

    const optionIcon = useMemo(() => {
        if (descriptor) {
            return makeFontAwesomeIcon(descriptor.upgradeIconPrefix, descriptor.upgradeIcon);
        }

        if (formattedAccessLocation === 'Drive Up Access') {
            return <DriveUp />;
        }

        if (option.floor === 0) {
            return <Level0 />;
        }

        return <Level1 />;
    }, [formattedAccessLocation, descriptor, option.floor]);

    /**
     * The idea here is that we only pass the deal explicity to this component if
     * it is a UnitType->UnitType upsell. If that is passed down we return that deal.
     *
     * If it is a Descriptor->Descriptor upsell such as CRUK or CNCK, we need to
     * find the same deal from the next descriptor OR return the first deal from
     * the descriptor if it is not found.
     */
    const dealObject = useMemo((): Deal | undefined => {
        if (dealObjectFromProps) return dealObjectFromProps;

        const deals = option.deals ?? [];

        if (reservationData?.dealObject && deals.length > 0) {
            const dealFromOption = deals.find(
                (deal) => deal._id === reservationData.dealObject?._id,
            );

            return dealFromOption ?? deals[0];
        }

        return undefined;
    }, [dealObjectFromProps, option.deals, reservationData]);

    /**
     * Helper memo for displaying the price difference between the selected unitType and the upsell unitType.
     * When a matching deal exists, uses deal's discountedRate.
     * When no matching deal, uses rates.discountedRate — always the consumer-facing price
     * (equals deal price when discounted, equals rates.rate when not discounted).
     */
    const priceDifferenceDisplay = useMemo(() => {
        const optionRate = dealObject
            ? getEffectiveDealRate(dealObject, option.rates.rate)
            : option.rates.rate;
        const sourceRate = initialRate ?? reservationData?.unitType.rates.rate ?? 0;
        const sourceDeal = reservationData?.dealObject;

        return getPriceDifferenceDisplay(
            sourceRate,
            optionRate,
            sourceDeal,
            dealObject,
            { isDefault: defaultOption, showTotal: showTotalPrice },
        );
    }, [dealObject, defaultOption, getPriceDifferenceDisplay, initialRate, option.rates.rate, reservationData, showTotalPrice]);

    const handleSelectedChange = () => {
        firebaseLogEvent('unit_option_selected', {});
        if (onOptionSelect) {
            onOptionSelect(option, descriptor);
        } else {
            setSelectedID(option._id);
            setSelectedUnitType(option);
            if (descriptor) {
                setSelectedDescriptor(descriptor);
            }
        }
    };

    return (
        <div
            className={`${classes.options} ${desktopOption === true && classes.desktopOption
            } ${dealObject && classes.deal_option} ${wizard && classes.wizard_option
            }`}
            data-testid="unit-option"
            data-unit-type-id={option._id}
            onClick={handleSelectedChange}
        >
            {dealObject && desktopOption && (
                <div className={`${classes.deal_banner}`}>
                    <span className={`${classes.title}`}>{dealObject?.title}</span>
                </div>
            )}
            <div
                className={`${classes.option_content} ${(dealObject || descriptor) && classes.deal_option_content}`}
            >
                <div className={`${classes.icon}`}>
                    {optionIcon}
                </div>
                <div className={`${classes.copy}`}>
                    <span className={`${classes.header}`}>{optionHeader}</span>
                    {!descriptor && (<label className={`${classes.label}`}>{optionLabel}</label>)}
                </div>
                <div className={`${classes.price_container}`}>
                    <div className={`${classes.price}`}>
                        <span className={`${classes.display}`}>
                            {priceDifferenceDisplay}
                        </span>
                    </div>
                    <div className={`${classes.radio_wrapper}`}>
                        <input
                            className={`${classes.radio}`}
                            type="checkbox"
                            checked={selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={handleSelectedChange}
                        />
                    </div>
                    {dealObject && (
                        <div className={`${unitListingClasses.deal_copy} ${classes.deal_copy}`}>
                            <span className={unitListingClasses.text}>
                                {dealObject.customFormat?.primary.length
                                    ? formatDealCustomText(dealObject, location, dealObject.customFormat?.primary)
                                    : getDealDefaultText(dealObject, location)}
                            </span>
                            <span className={unitListingClasses.text}>
                                {formatDealCustomText(
                                    dealObject,
                                    location,
                                    dealObject.customFormat?.secondary,
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {descriptor && (<label className={`${classes.descriptor_upsell_label}`}>{optionLabel}</label>)}
        </div>
    );
}
